import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../app/store';
import { refreshEntitlements } from '../app/slices/subscriptionSlice';
import { useAppDispatch } from '../app/hooks';
import { useEntitlements } from '../app/hooks/useEntitlements';
import PaywallModal from './PaywallModal';
import LoadingSpinner from './LoadingSpinner';
import { signOut } from '../app/slices/authSlice';

export type EntitlementLevel = 'PRO' | 'ARCHIVE' | 'ADMIN';

interface EntitlementGuardProps {
  children: React.ReactNode;
  required: EntitlementLevel;
  fallback?: React.ReactNode;
}

const EntitlementGuard: React.FC<EntitlementGuardProps> = ({ children, required, fallback }) => {
  const dispatch = useAppDispatch();
  const { plan, isLoading: subscriptionLoading } = useSelector(
    (state: RootState) => state.subscription
  );
  const { isAdmin } = useEntitlements();
  const { isAuthenticated, initialized, user } = useSelector((state: RootState) => state.auth);

  const [showPaywall, setShowPaywall] = useState(false);
  const [hasCheckedEntitlements, setHasCheckedEntitlements] = useState(false);
  const [lastRefreshAt, setLastRefreshAt] = useState<number | null>(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);

  // **OPTIMIZED AUTH FLOW**: Check subscription status immediately after auth
  useEffect(() => {
    if (!isAuthenticated || !initialized || !user) {
      setHasCheckedEntitlements(false);
      setIsCheckingAccess(false);
      return;
    }

    // Wait for subscription data to load first (prevent premature checks)
    if (subscriptionLoading) {
      console.log('[EntitlementGuard] Waiting for subscription data to load...');
      return;
    }

    // Skip if already checked or currently checking
    if (hasCheckedEntitlements || isCheckingAccess) {
      return;
    }

    const checkAccessSequence = async () => {
      setIsCheckingAccess(true);
      console.log('[EntitlementGuard] Starting access check sequence...', {
        plan,
        subscriptionLoading,
      });

      try {
        // Step 1: Check if plan is already loaded from Firestore (during auth init)
        if (plan && plan !== 'FREE') {
          console.log(
            '[EntitlementGuard] ✅ Plan already loaded from Firestore:',
            plan,
            '- Granting immediate access'
          );
          // Set both flags synchronously to prevent showing paywall
          setHasCheckedEntitlements(true);
          setIsCheckingAccess(false);
          return;
        }

        // Step 2: Check throttling
        const now = Date.now();
        const THROTTLE_MS = 60_000; // 1 minute throttle
        if (lastRefreshAt && now - lastRefreshAt < THROTTLE_MS) {
          console.log('[EntitlementGuard] Throttled, using cached entitlements, plan:', plan);
          setHasCheckedEntitlements(true);
          setIsCheckingAccess(false);
          return;
        }

        // Step 3: Refresh entitlements from Stripe
        console.log('[EntitlementGuard] Plan is FREE or not loaded, refreshing from Stripe...');
        const resultAction = await dispatch(refreshEntitlements());
        setLastRefreshAt(now);

        // Step 4: Handle refresh errors
        if (refreshEntitlements.rejected.match(resultAction)) {
          const error = resultAction.payload as string;
          console.error('[EntitlementGuard] ❌ Entitlement refresh failed:', error);

          // Only sign out for critical auth errors
          if (
            error &&
            (error.includes('auth/internal-error') || error.includes('auth/user-not-found'))
          ) {
            console.warn('[EntitlementGuard] Critical auth error, signing out');
            dispatch(signOut());
          }
          setHasCheckedEntitlements(true); // Mark as checked even on error
        } else {
          console.log('[EntitlementGuard] ✅ Access check complete, final plan:', plan);
          setHasCheckedEntitlements(true);
        }
      } catch (error) {
        console.error('[EntitlementGuard] ❌ Access check exception:', error);
        setHasCheckedEntitlements(true); // Mark as checked to avoid infinite loop
      } finally {
        setIsCheckingAccess(false);
      }
    };

    // Small delay to allow auth.currentUser to fully initialize
    const timer = setTimeout(() => {
      checkAccessSequence();
    }, 300);

    return () => clearTimeout(timer);
  }, [
    dispatch,
    isAuthenticated,
    initialized,
    user,
    plan,
    subscriptionLoading,
    hasCheckedEntitlements,
    isCheckingAccess,
    lastRefreshAt,
  ]);

  // Show loading while checking access
  if (
    !initialized ||
    (isAuthenticated && (isCheckingAccess || subscriptionLoading || !hasCheckedEntitlements))
  ) {
    return <LoadingSpinner size="lg" text="Checking access..." />;
  }

  // If not authenticated, this should be handled by ProtectedRoute
  if (!isAuthenticated) {
    return null;
  }

  // Check entitlements
  const hasRequiredEntitlement = () => {
    // Admin access - check custom claims
    if (required === 'ADMIN') {
      const adminAccess = isAuthenticated && !!isAdmin;
      console.log(`[EntitlementGuard] Admin check: ${adminAccess}`);
      return adminAccess;
    }

    // FREE users have no entitlements
    if (plan === 'FREE') {
      console.log('[EntitlementGuard] ❌ User has FREE plan, denying access');
      return false;
    }

    // PRO plans (including trial) have full access
    if (plan === 'PRO-MONTHLY' || plan === 'PRO-ANNUAL' || plan === 'PRO_TRIAL') {
      console.log(`[EntitlementGuard] ✅ User has PRO plan (${plan}), granting access`);
      return true;
    }

    // ARCHIVE plans have limited access
    if (plan === 'ARCHIVE' || plan === 'ARCHIVE-ONE-TIME') {
      const archiveAccess = required === 'ARCHIVE';
      console.log(
        `[EntitlementGuard] Archive plan, required=${required}, granting=${archiveAccess}`
      );
      return archiveAccess;
    }

    console.log(`[EntitlementGuard] ⚠️ Unknown plan state: "${plan}", denying access`);
    return false;
  };

  const hasAccess = hasRequiredEntitlement();

  // If user has access, render children
  if (hasAccess) {
    return <>{children}</>;
  }

  // If user doesn't have access, show fallback or paywall
  if (fallback) {
    return <>{fallback}</>;
  }

  // Show paywall modal for FREE users trying to access PRO features
  if (plan === 'FREE') {
    return (
      <>
        <PaywallModal isOpen={!showPaywall} onClose={() => setShowPaywall(true)} />
        {showPaywall && (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Upgrade Required</h2>
              <p className="text-gray-600 mb-6">This feature requires a Pro subscription.</p>
              <button onClick={() => setShowPaywall(false)} className="btn-primary">
                View Plans
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  // For ARCHIVE users trying to access PRO features
  if ((plan === 'ARCHIVE' || plan === 'ARCHIVE-ONE-TIME') && required === 'PRO') {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Read-Only Access</h2>
          <p className="text-gray-600 mb-6">
            Your account is on the Archive plan, which provides read-only access to your existing
            data. You cannot add new expenses, sessions, or generate new reports.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
            <p className="text-sm text-yellow-800">
              <strong>Available actions:</strong> View expenses, sessions, and existing reports.
              Download data exports.
            </p>
          </div>
          <p className="text-sm text-gray-500">Contact support to upgrade your plan.</p>
        </div>
      </div>
    );
  }

  // Fallback for any other cases
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Restricted</h2>
        <p className="text-gray-600">You don't have permission to access this feature.</p>
      </div>
    </div>
  );
};

// HOC version for easier usage
export function withEntitlementGuard<P extends object>(
  Component: React.ComponentType<P>,
  required: EntitlementLevel,
  fallback?: React.ReactNode
) {
  return (props: P) => (
    <EntitlementGuard required={required} fallback={fallback}>
      <Component {...props} />
    </EntitlementGuard>
  );
}

export default EntitlementGuard;
